import * as Yup from 'yup'

import { diagnosticTraceProblem } from '../../../helpers/diagnosticTrace'

/**
 * Validation for the create form, replacing a single `canSubmit` boolean.
 *
 * The boolean disabled the button and explained nothing: an admin who mistyped
 * one character of a 32-character trace id, or left one of eight conditionally
 * required fields blank, got a dead control and no diagnosis. Every rule here
 * carries the message the operator sees, as a key the form translates.
 *
 * A FUNCTION of `isAdmin` rather than a schema reading `$isAdmin` from Yup
 * context: the role does not change while the form is open, so threading it as
 * validation context buys nothing and Yup 0.32's typing for a multi-key
 * `.when()` mixing a context ref with a sibling field is not worth the fight.
 */
export function buildSupportTicketCreateValidationSchema(isAdmin: boolean) {
  const entityRequired = (schema: Yup.StringSchema) =>
    schema.when('reporterMode', {
      // An admin filing on their own behalf inherits the entity from their
      // token; everyone else must name the entity the ticket is about.
      is: (reporterMode: string) => isAdmin && reporterMode === 'CALLER',
      then: (branch: Yup.StringSchema) => branch,
      otherwise: (branch: Yup.StringSchema) =>
        branch.trim().required('This field is required')
    })

  return Yup.object({
    title: Yup.string().trim().required('This field is required'),
    productScope: Yup.string().trim().required('This field is required'),
    confirmAffectedEntities: Yup.boolean().test(
      'confirm-visibility',
      'Confirm who may view this ticket.',
      function (value) {
        return !this.parent.affectedEntities?.length || value === true
      }
    ),
    body: Yup.string().trim().required('This field is required'),

    entityType: entityRequired(Yup.string()),
    entityCode: entityRequired(Yup.string()),

    customerUserId: Yup.string().when('reporterMode', {
      is: 'CUSTOMER_USER',
      then: (branch: Yup.StringSchema) =>
        branch.trim().required('This field is required'),
      otherwise: (branch: Yup.StringSchema) => branch
    }),

    externalName: Yup.string().when('reporterMode', {
      is: 'EXTERNAL',
      then: (branch: Yup.StringSchema) =>
        branch.trim().required('This field is required'),
      otherwise: (branch: Yup.StringSchema) => branch
    }),

    // Either channel satisfies the requirement, so the rule lives on one field
    // and reads the other. `required` on both would demand both.
    externalEmail: Yup.string().test(
      'contact-channel',
      'Enter an email or a phone number.',
      function validateContactChannel(value) {
        const { reporterMode, externalPhone } = this.parent as Record<
          string,
          string
        >
        if (reporterMode !== 'EXTERNAL') return true

        return Boolean(value?.trim() || externalPhone?.trim())
      }
    ),

    // The seven trace rules stay in diagnosticTraceProblem, which is what
    // buildDiagnosticTraceInput consults too — one source, so the message the
    // operator reads and the payload the server receives cannot disagree.
    traceId: Yup.string().test(
      'diagnostic-trace',
      'Trace details are incomplete.',
      function validateTrace() {
        const parent = this.parent as Record<string, string>
        const problem = diagnosticTraceProblem(
          Boolean(parent.attachTrace),
          parent.traceId || '',
          parent.traceFrom || '',
          parent.traceTo || '',
          parent.traceServices || ''
        )

        return problem ? this.createError({ message: problem }) : true
      }
    )
  })
}
